/**
 * CustomerSendEmailModal
 *
 * A two-step email modal used exclusively from the Customers page.
 * Step 1 – Select Template: template picker, subject, message body, attach invoices toggle.
 * Step 2 – Email Recipients: tabular view with Account, Customer, To, CC columns.
 *
 * NOTE: This component is intentionally separate from the shared SendEmailModal used by
 * other flows (Collections Queue, Promises, Cases, etc.) to allow independent iteration
 * and stakeholder sign-off before wider rollout.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { ChevronLeft, ArrowRight, Plus, X, Mail, User, Check } from "lucide-react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import type { Editor } from "@tiptap/react";
import { useTemplatesList, useTemplateById } from "@/hooks/useTemplatesData";
import { useCustomerContactsList } from "@/hooks/useCustomerContacts";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { VariableExtension } from "@/lib/tiptap-variable-extension";
import { cn } from "@/lib/utils";
import type { ContactUI } from "@/types/ui/customer-detail";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function stripHtml(html: string): string {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

const convertVariablesToNodes = (html: string): string => {
  const normalized = html.replace(
    /<span[^>]*data-variable="([^"]+)"[^>]*>[^<]*<\/span>/g,
    "$1"
  );
  const variableRegex = /\{\{([^}]+)\}\}/g;
  return normalized.replace(variableRegex, (match) => {
    return `<span data-variable="${match}" class="variable-node"></span>`;
  });
};

const convertVariableNodesToText = (html: string): string => {
  return html
    .replace(
      /<span[^>]*data-variable="([^"]+)"[^>]*>[^<]*<\/span>/g,
      "$1"
    )
    .replace(/<\/?p>/g, "")
    .trim();
};

const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// ─── CompactEmailSelector ─────────────────────────────────────────────────────
// Used exclusively in the Step 2 recipient table.
// Email badges are shown inside the field. A small teal + button sits in the
// top-right corner to open the contacts/custom-email popover — no inline
// "Add recipient…" row that would grow the cell height.

interface CompactEmailSelectorProps {
  selectedEmails: string[];
  onEmailsChange: (emails: string[]) => void;
  contacts?: ContactUI[];
  required?: boolean;
  placeholder?: string;
}

function CompactEmailSelector({
  selectedEmails,
  onEmailsChange,
  contacts = [],
  required = false,
  placeholder = "No recipients yet",
}: Readonly<CompactEmailSelectorProps>) {
  const [open, setOpen] = useState(false);
  const [customEmail, setCustomEmail] = useState("");
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const customInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showCustomInput && customInputRef.current) {
      customInputRef.current.focus();
    }
  }, [showCustomInput]);

  const handleRemove = (email: string) => {
    if (required && selectedEmails.length === 1) return;
    onEmailsChange(selectedEmails.filter((e) => e !== email));
  };

  const handleAddContact = (contact: ContactUI) => {
    if (!selectedEmails.includes(contact.email)) {
      onEmailsChange([...selectedEmails, contact.email]);
    }
    setSearchQuery("");
  };

  const handleAddCustomEmail = () => {
    const trimmed = customEmail.trim();
    if (trimmed && isValidEmail(trimmed) && !selectedEmails.includes(trimmed)) {
      onEmailsChange([...selectedEmails, trimmed]);
      setCustomEmail("");
      setShowCustomInput(false);
      setOpen(false);
    }
  };

  const getContactName = (email: string) => {
    const c = contacts.find((x) => x.email === email);
    return c ? `${c.firstName} ${c.surname}` : null;
  };

  const isPrimaryContact = (email: string) =>
    contacts.find((x) => x.email === email)?.isPrimary ?? false;

  const filteredContacts = contacts.filter((c) => {
    const fullName = `${c.firstName} ${c.surname}`.toLowerCase();
    const q = searchQuery.toLowerCase();
    return (
      fullName.includes(q) ||
      c.email.toLowerCase().includes(q) ||
      c.jobTitle?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="relative w-full">
      {/* Field box — fixed width, expands vertically only */}
      <div className="relative border rounded-md bg-background min-h-[42px] p-2 pr-9 w-full overflow-hidden">
        {selectedEmails.length === 0 ? (
          <span className="text-xs text-muted-foreground">{placeholder}</span>
        ) : (
          <div className="flex flex-wrap gap-1 w-full">
            {selectedEmails.map((email) => {
              const name = getContactName(email);
              const isPrimary = isPrimaryContact(email);
              const canRemove = !required || selectedEmails.length > 1;
              return (
                <Badge
                  key={email}
                  variant={isPrimary ? "default" : "secondary"}
                  className={cn(
                    "pl-1.5 pr-1 py-0.5 gap-1 text-xs max-w-full",
                    isPrimary && "border-primary"
                  )}
                >
                  <Mail className="h-3 w-3 shrink-0" />
                  <span className="truncate" style={{ maxWidth: "calc(100% - 2rem)" }}>
                    {name ? `${name} <${email}>` : email}
                  </span>
                  {canRemove && (
                    <button
                      type="button"
                      onClick={() => handleRemove(email)}
                      className="ml-0.5 rounded-full hover:bg-muted p-0.5 shrink-0"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </Badge>
              );
            })}
          </div>
        )}

        {/* Teal + button — top-right corner */}
        <Popover
          open={open}
          onOpenChange={(v) => {
            setOpen(v);
            if (!v) {
              setShowCustomInput(false);
              setCustomEmail("");
              setSearchQuery("");
            }
          }}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              className="absolute top-1.5 right-1.5 flex items-center justify-center h-6 w-6 rounded-sm bg-teal-600 hover:bg-teal-700 text-white transition-colors"
              aria-label="Add email"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[360px] p-0" align="end">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Search contacts…"
                value={searchQuery}
                onValueChange={setSearchQuery}
              />
              <CommandList>
                <CommandEmpty>No contacts found.</CommandEmpty>
                {filteredContacts.length > 0 && (
                  <CommandGroup heading="Customer Contacts">
                    <ScrollArea className="h-[200px]">
                      {filteredContacts.map((contact) => {
                        const isSelected = selectedEmails.includes(contact.email);
                        return (
                          <CommandItem
                            key={contact.id}
                            onSelect={() => handleAddContact(contact)}
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <div
                              className={cn(
                                "flex h-4 w-4 items-center justify-center rounded-sm border border-primary shrink-0",
                                isSelected
                                  ? "bg-primary text-primary-foreground"
                                  : "opacity-50 [&_svg]:invisible"
                              )}
                            >
                              <Check className="h-3 w-3" />
                            </div>
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted shrink-0">
                                <User className="h-4 w-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium truncate text-sm">
                                    {contact.firstName} {contact.surname}
                                  </span>
                                  {contact.isPrimary && (
                                    <Badge variant="outline" className="text-xs">
                                      Primary
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-xs text-muted-foreground truncate">
                                  {contact.jobTitle && (
                                    <span>{contact.jobTitle} • </span>
                                  )}
                                  {contact.email}
                                </div>
                              </div>
                            </div>
                          </CommandItem>
                        );
                      })}
                    </ScrollArea>
                  </CommandGroup>
                )}
                <CommandSeparator />
                <CommandGroup>
                  {!showCustomInput ? (
                    <CommandItem
                      onSelect={() => setShowCustomInput(true)}
                      className="cursor-pointer"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add custom email address
                    </CommandItem>
                  ) : (
                    <div className="p-2 space-y-2">
                      <Input
                        ref={customInputRef}
                        type="email"
                        placeholder="email@example.com"
                        value={customEmail}
                        onChange={(e) => setCustomEmail(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddCustomEmail();
                          } else if (e.key === "Escape") {
                            setShowCustomInput(false);
                            setCustomEmail("");
                          }
                        }}
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleAddCustomEmail}
                          disabled={
                            !customEmail.trim() ||
                            !isValidEmail(customEmail.trim())
                          }
                        >
                          Add
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setShowCustomInput(false);
                            setCustomEmail("");
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {required && selectedEmails.length === 0 && (
        <p className="text-xs text-destructive mt-1">
          At least one recipient is required
        </p>
      )}
    </div>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CustomerEmailRecipient {
  customerId: string;
  accountNumber: string;
  customerName: string;
  toEmails: string[];
  ccEmails: string[];
}

export interface CustomerSendEmailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** For single customer mode */
  customerId?: string;
  customerName?: string;
  customerEmail?: string;
  accountNumber?: string;
  /** For bulk mode */
  isBulkEmail?: boolean;
  bulkCustomers?: Array<{
    id: string;
    name: string;
    accountNumber: string;
    contact: string;
  }>;
  onSuccess?: () => void;
}

// ─── Per-customer recipient row (Step 2) ─────────────────────────────────────

interface RecipientRowProps {
  customerId: string;
  accountNumber: string;
  customerName: string;
  defaultEmail: string;
  toEmails: string[];
  ccEmails: string[];
  onToChange: (emails: string[]) => void;
  onCcChange: (emails: string[]) => void;
  checked: boolean;
  onToggle: () => void;
}

function RecipientRow({
  customerId,
  accountNumber,
  customerName,
  toEmails,
  ccEmails,
  onToChange,
  onCcChange,
  checked,
  onToggle,
}: Readonly<RecipientRowProps>) {
  const { data: contacts = [] } = useCustomerContactsList(customerId);

  return (
    <TableRow className={cn(!checked && "opacity-40")}>
      <TableCell className="align-top py-3 w-[44px] max-w-[44px] overflow-hidden">
        <Checkbox
          checked={checked}
          onCheckedChange={onToggle}
          aria-label={`Include ${customerName} in email run`}
        />
      </TableCell>
      <TableCell className="align-top py-3 w-[120px] max-w-[120px] overflow-hidden text-sm">
        <span className="block truncate">{accountNumber}</span>
      </TableCell>
      <TableCell className="align-top py-3 w-[200px] max-w-[200px] overflow-hidden text-sm">
        <span className="block truncate">{customerName}</span>
      </TableCell>
      <TableCell className="align-top py-2 overflow-hidden">
        <CompactEmailSelector
          selectedEmails={toEmails}
          onEmailsChange={onToChange}
          contacts={contacts}
          required={true}
          placeholder="No recipients yet"
        />
      </TableCell>
      <TableCell className="align-top py-2 overflow-hidden">
        <CompactEmailSelector
          selectedEmails={ccEmails}
          onEmailsChange={onCcChange}
          contacts={contacts}
          required={false}
          placeholder="No CC recipients"
        />
      </TableCell>
    </TableRow>
  );
}

// ─── Main modal (shell) ───────────────────────────────────────────────────────

export function CustomerSendEmailModal({
  open,
  onOpenChange,
  customerId,
  customerName = "",
  customerEmail = "",
  accountNumber = "",
  isBulkEmail = false,
  bulkCustomers = [],
  onSuccess,
}: Readonly<CustomerSendEmailModalProps>) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && (
        <CustomerSendEmailModalContent
          onOpenChange={onOpenChange}
          customerId={customerId}
          customerName={customerName}
          customerEmail={customerEmail}
          accountNumber={accountNumber}
          isBulkEmail={isBulkEmail}
          bulkCustomers={bulkCustomers}
          onSuccess={onSuccess}
        />
      )}
    </Dialog>
  );
}

// ─── Modal content (unmounts on close to reset all state) ─────────────────────

function CustomerSendEmailModalContent({
  onOpenChange,
  customerId,
  customerName,
  customerEmail,
  accountNumber,
  isBulkEmail,
  bulkCustomers,
  onSuccess,
}: Readonly<Omit<CustomerSendEmailModalProps, "open">>) {
  // ── Step ───────────────────────────────────────────────────────────────────
  const [step, setStep] = useState<1 | 2>(1);

  // ── Step 1 state ───────────────────────────────────────────────────────────
  const [template, setTemplate] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [subjectHtml, setSubjectHtml] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [attachInvoices, setAttachInvoices] = useState(false);

  const originalTemplateSubject = useRef<string>("");
  const originalTemplateBody = useRef<string>("");
  const bodyEditorRef = useRef<Editor | null>(null);

  // ── Step 2 state ───────────────────────────────────────────────────────────
  const [recipients, setRecipients] = useState<
    Record<string, { toEmails: string[]; ccEmails: string[] }>
  >({});
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());

  // ── Data hooks ─────────────────────────────────────────────────────────────
  const { data: templatesData } = useTemplatesList();
  const templates = Array.isArray(templatesData) ? templatesData : [];
  const { data: templateDetail } = useTemplateById(selectedTemplateId);

  const { data: singleContacts = [] } = useCustomerContactsList(
    !isBulkEmail && customerId ? customerId : "0"
  );

  // ── TipTap subject editor ──────────────────────────────────────────────────
  const subjectEditor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        bulletList: false,
        orderedList: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
      }),
      Placeholder.configure({ placeholder: "Payment Reminder" }),
      VariableExtension,
    ],
    content: "",
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none p-3 min-h-[44px]",
      },
    },
    onUpdate: ({ editor }) => {
      setSubjectHtml(editor.getHTML());
    },
  });

  const handleBodyEditorReady = useCallback((editor: Editor) => {
    bodyEditorRef.current = editor;
  }, []);

  // ── Seed recipients on entering Step 2 ────────────────────────────────────
  const initRecipients = useCallback(
    (contacts: ContactUI[]) => {
      if (isBulkEmail) {
        const init: Record<string, { toEmails: string[]; ccEmails: string[] }> = {};
        bulkCustomers!.forEach((c) => {
          if (!recipients[c.id]) {
            init[c.id] = { toEmails: c.contact ? [c.contact] : [], ccEmails: [] };
          }
        });
        if (Object.keys(init).length > 0) {
          setRecipients((prev) => ({ ...init, ...prev }));
        }
        // Tick all customers by default
        setCheckedIds(new Set(bulkCustomers!.map((c) => c.id)));
      } else if (customerId) {
        if (!recipients[customerId]) {
          const primary = contacts.find((x) => x.isPrimary) || contacts[0];
          const seed = customerEmail || primary?.email || "";
          setRecipients((prev) => ({
            ...prev,
            [customerId]: { toEmails: seed ? [seed] : [], ccEmails: [] },
          }));
        }
        // Tick the single customer by default
        setCheckedIds(new Set([customerId]));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isBulkEmail, bulkCustomers, customerId, customerEmail]
  );

  // ── Populate editors when template is selected ─────────────────────────────
  useEffect(() => {
    if (templateDetail && selectedTemplateId) {
      const rawSubject = templateDetail.subject ?? "";
      const convertedSubject = convertVariablesToNodes(rawSubject);
      const rawBody = templateDetail.content ?? "";
      const convertedBody = convertVariablesToNodes(rawBody);

      if (subjectEditor && !subjectEditor.isDestroyed) {
        subjectEditor.commands.setContent(convertedSubject);
        originalTemplateSubject.current = subjectEditor.getHTML();
      }
      setSubjectHtml(convertedSubject);

      if (bodyEditorRef.current && !bodyEditorRef.current.isDestroyed) {
        bodyEditorRef.current.commands.setContent(convertedBody);
        const rendered = bodyEditorRef.current.getHTML();
        originalTemplateBody.current = rendered;
        setBodyHtml(rendered);
      } else {
        setBodyHtml(convertedBody);
        originalTemplateBody.current = convertedBody;
      }
    }
  }, [templateDetail, selectedTemplateId, subjectEditor]);

  // ── Template change ────────────────────────────────────────────────────────
  const handleTemplateChange = (value: string) => {
    setTemplate(value);
    if (value === "none") {
      setSelectedTemplateId(null);
      setSubjectHtml("");
      originalTemplateSubject.current = "";
      originalTemplateBody.current = "";
      if (subjectEditor && !subjectEditor.isDestroyed) {
        subjectEditor.commands.setContent("");
      }
      if (bodyEditorRef.current && !bodyEditorRef.current.isDestroyed) {
        bodyEditorRef.current.commands.setContent("");
      }
      setBodyHtml("");
    } else {
      setSelectedTemplateId(value);
    }
  };

  // ── Step 1 → 2 ────────────────────────────────────────────────────────────
  const handleNext = () => {
    const currentSubjectHtml = subjectEditor?.getHTML() || subjectHtml;
    convertVariableNodesToText(currentSubjectHtml); // kept for future validation
    const bodyEditor = bodyEditorRef.current;
    const _isBodyEmpty =
      bodyEditor && !bodyEditor.isDestroyed ? bodyEditor.isEmpty : !stripHtml(bodyHtml).trim();
    void _isBodyEmpty; // suppress unused warning — validation deferred
    initRecipients(singleContacts);
    setStep(2);
  };

  // ── Recipient helpers ──────────────────────────────────────────────────────
  const updateTo = (id: string, emails: string[]) =>
    setRecipients((prev) => ({
      ...prev,
      [id]: { ...(prev[id] ?? { toEmails: [], ccEmails: [] }), toEmails: emails },
    }));

  const updateCc = (id: string, emails: string[]) =>
    setRecipients((prev) => ({
      ...prev,
      [id]: { ...(prev[id] ?? { toEmails: [], ccEmails: [] }), ccEmails: emails },
    }));

  // ── Checkbox helpers ──────────────────────────────────────────────────────
  const toggleCustomer = (id: string) =>
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // ── Derived ────────────────────────────────────────────────────────────────
  const displayedCustomers = isBulkEmail
    ? bulkCustomers!
    : customerId
    ? [
        {
          id: customerId,
          name: customerName ?? "",
          accountNumber: accountNumber ?? "",
          contact: customerEmail ?? "",
        },
      ]
    : [];

  const allChecked =
    displayedCustomers.length > 0 &&
    displayedCustomers.every((c) => checkedIds.has(c.id));
  const someChecked =
    !allChecked && displayedCustomers.some((c) => checkedIds.has(c.id));

  const toggleAll = () => {
    if (allChecked) {
      setCheckedIds(new Set());
    } else {
      setCheckedIds(new Set(displayedCustomers.map((c) => c.id)));
    }
  };

  const step2Title = isBulkEmail
    ? `Email Recipients (${bulkCustomers!.length} customers)`
    : "Email Recipients";

  // ── Step 1 render ──────────────────────────────────────────────────────────
  if (step === 1) {
    return (
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Send Email</DialogTitle>
          <DialogDescription>
            {isBulkEmail
              ? `Step 1 of 2 — Compose email for ${bulkCustomers!.length} customer${
                  bulkCustomers!.length !== 1 ? "s" : ""
                }`
              : `Step 1 of 2 — Compose email for ${customerName}`}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 py-2 overflow-y-auto flex-1 min-h-0">
          {/* Template */}
          <div className="grid gap-2">
            <Label htmlFor="cse-template">Template (Optional)</Label>
            <Select value={template} onValueChange={handleTemplateChange}>
              <SelectTrigger id="cse-template">
                <SelectValue placeholder="Select a template" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Subject */}
          <div className="grid gap-2">
            <Label>Subject</Label>
            {subjectEditor ? (
              <div className="border rounded-md">
                <EditorContent
                  editor={subjectEditor}
                  className="prose prose-sm max-w-none [&_.ProseMirror]:p-3 [&_.ProseMirror]:min-h-[44px] [&_.ProseMirror]:focus:outline-none"
                />
              </div>
            ) : (
              <div className="border rounded-md p-3 min-h-[44px] text-muted-foreground">
                Loading editor…
              </div>
            )}
          </div>

          {/* Message */}
          <div className="grid gap-2">
            <Label>Message</Label>
            <RichTextEditor
              content={bodyHtml}
              onChange={setBodyHtml}
              onEditorReady={handleBodyEditorReady}
              placeholder="Enter your message…"
            />
          </div>

          {/* Attach outstanding invoices */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="cse-attach-invoices"
              checked={attachInvoices}
              onCheckedChange={(checked) => setAttachInvoices(checked === true)}
            />
            <Label htmlFor="cse-attach-invoices" className="text-sm font-normal">
              Attach outstanding invoices
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleNext}>
            Next
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    );
  }

  // ── Step 2 render ──────────────────────────────────────────────────────────
  return (
    <DialogContent className="sm:max-w-[1200px] h-[90vh] flex flex-col overflow-hidden">
      <DialogHeader>
        <DialogTitle>{step2Title}</DialogTitle>
        <DialogDescription>
          Step 2 of 2 — Review and configure recipients for each customer
        </DialogDescription>
      </DialogHeader>

      {/* Table with sticky header and scrollable body */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden rounded-md border">
        {/* Sticky column header — never scrolls */}
        <div className="shrink-0 border-b bg-muted/50">
          <Table className="table-fixed w-full">
            <colgroup>
              <col className="w-[44px]" />  {/* Checkbox */}
              <col className="w-[120px]" />
              <col className="w-[200px]" />
              <col /> {/* To — flex fills remaining */}
              <col /> {/* CC — flex fills remaining */}
            </colgroup>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[44px]">
                  <Checkbox
                    checked={allChecked}
                    ref={(el) => {
                      if (el) (el as HTMLButtonElement & { indeterminate?: boolean }).indeterminate = someChecked;
                    }}
                    onCheckedChange={toggleAll}
                    aria-label="Select all customers"
                  />
                </TableHead>
                <TableHead className="w-[120px]">Account</TableHead>
                <TableHead className="w-[200px]">Customer</TableHead>
                <TableHead>To</TableHead>
                <TableHead>CC</TableHead>
              </TableRow>
            </TableHeader>
          </Table>
        </div>
        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1">
          <Table className="table-fixed w-full">
            <colgroup>
              <col className="w-[44px]" />  {/* Checkbox */}
              <col className="w-[120px]" />
              <col className="w-[200px]" />
              <col /> {/* To — flex fills remaining */}
              <col /> {/* CC — flex fills remaining */}
            </colgroup>
            <TableBody>
              {displayedCustomers.map((c) => (
                <RecipientRow
                  key={c.id}
                  customerId={c.id}
                  accountNumber={c.accountNumber}
                  customerName={c.name}
                  defaultEmail={c.contact}
                  toEmails={
                    recipients[c.id]?.toEmails ?? (c.contact ? [c.contact] : [])
                  }
                  ccEmails={recipients[c.id]?.ccEmails ?? []}
                  onToChange={(emails) => updateTo(c.id, emails)}
                  onCcChange={(emails) => updateCc(c.id, emails)}
                  checked={checkedIds.has(c.id)}
                  onToggle={() => toggleCustomer(c.id)}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <DialogFooter>
        <Button
          variant="outline"
          onClick={() => setStep(1)}
          className="mr-auto"
        >
          <ChevronLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            // Placeholder — will hand off to platform emailing MFE in a future step
            onSuccess?.();
            onOpenChange(false);
          }}
        >
          Next
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
